import { compact, filter, map, orderBy, partition, reduce } from "lodash/fp";

import type {
  ApiActorCitation,
  ApiChannelProvider,
  ApiEntity,
  ApiEntityActor,
  ApiTopic,
  ApiTopicEntity,
  EntityKind,
  Topic,
  TopicChannelProvider,
  TopicContributor,
  TopicEntity,
  TopicEntityContributor,
} from "@app/analytics/types";
import { PRODUCTIVITY_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import { PartialBy } from "@app/common/types";
import { ProductivityProviderId } from "@app/common/types/providerTypes";
import { isEmailProvider } from "@app/common/utils/providerUtils";
import {
  parseUTCDateTimeFromString,
  optionalParseUTCDateTimeFromString,
} from "@app/utils/utils";

const CHILD_KIND_TO_ENTITY_KIND: { [key in EntityKind]?: EntityKind } = {
  key_question: "key_question_answer",
};

export const API_CHANNEL_PROVIDER_TO_PROVIDER: {
  [key in ApiChannelProvider]: ProductivityProviderId;
} = {
  gmail: PRODUCTIVITY_PROVIDERS_BY_ID.google_email.id,
  outlook: PRODUCTIVITY_PROVIDERS_BY_ID.microsoft_graph_email.id,
  teams_channel:
    PRODUCTIVITY_PROVIDERS_BY_ID.microsoft_graph_teams_chat_ingest.id,
  teams_chat: PRODUCTIVITY_PROVIDERS_BY_ID.microsoft_graph_teams_chat_ingest.id,
  slack: PRODUCTIVITY_PROVIDERS_BY_ID.slack_ingest.id,
};

const getProvider = (provider: ApiChannelProvider): TopicChannelProvider =>
  API_CHANNEL_PROVIDER_TO_PROVIDER[provider] ?? "unknown";

const parseActorCitation = (data: ApiActorCitation): TopicContributor => ({
  id: data.entityActor.actor.id,
  kind: data.entityActor.kind,
  displayName: data.entityActor.actor.providerDisplayName,
  numContributions: data.citationCount,
  provider: getProvider(data.entityActor.actor.provider),
  providerUserId: data.entityActor.actor.providerUserId,
});

const parseEntityActor = (data: ApiEntityActor): TopicEntityContributor => ({
  id: data.actor.id,
  kind: data.kind,
  displayName: data.actor.providerDisplayName,
  provider: getProvider(data.actor.provider),
  providerUserId: data.actor.providerUserId,
});

const parseContributors = (rawContributors: ApiTopicEntity["entityActors"]) => {
  const contributors = map(parseEntityActor, rawContributors);
  const [authors, otherContributors] = partition(
    { kind: "author" },
    contributors
  );
  // we can expect there to be a single (or no) author; should never have multiple
  return { author: authors[0], contributors: otherContributors };
};

const parseEntity =
  (topicId: TopicEntity["topicId"], kind: ApiEntity["kind"]) =>
  (data: ApiEntity): PartialBy<TopicEntity, "startTime" | "endTime"> | null => {
    if (data.kind != kind) return null;

    const {
      id,
      startTime,
      endTime,
      entityActors,
      entityRank,
      children: _,
      ...entity
    } = data;

    const { author, contributors } = parseContributors(entityActors);

    return {
      ...entity,
      entityId: id,
      topicId,
      author,
      createdOn: parseUTCDateTimeFromString(entity.createdOn),
      startTime: optionalParseUTCDateTimeFromString(startTime),
      endTime: optionalParseUTCDateTimeFromString(endTime),
      contributors,
      rank: entityRank ?? 0,
      provider: getProvider(entity.provider),
      updatedOn: parseUTCDateTimeFromString(entity.updatedOn),
      children: [],
      citations:
        kind === "citation"
          ? []
          : compact(map(parseEntity(topicId, "citation"), data.children)),
    };
  };

export const parseTopic = (data: ApiTopic): Topic => {
  const {
    firstMentionTime,
    lastMentionTime,
    actorCitations,
    channelCount,
    ownerUserName,
    threadCount,
    ...topic
  } = data;

  const channelProviders = filter(
    (p) => p != "unknown",
    map(getProvider, topic.channelProviders)
  ) as ProductivityProviderId[];

  const primaryProvider = channelProviders[0] ?? "unknown";

  return {
    ...topic,
    provider: primaryProvider,
    channelProviders,
    firstMention: parseUTCDateTimeFromString(firstMentionTime),
    lastMention: parseUTCDateTimeFromString(lastMentionTime),
    channelCount:
      (isEmailProvider(primaryProvider) ? threadCount : channelCount) ?? 0,
    contributors: map(parseActorCitation, actorCitations),
    impersonationCount: topic.impersonationCount ?? 0,
    messageCount: topic.messageCount ?? 0,
    ownerName: ownerUserName,
    status: "active", // as of right now, all topics are active
  };
};

export const parseTopics = (data: ApiTopic[]): Topic[] => map(parseTopic, data);

export const parseTopicEntity = (data: ApiTopicEntity): TopicEntity => {
  const { entityActors, entityRank, children, ...topicEntity } = data;

  const childEntityKind = CHILD_KIND_TO_ENTITY_KIND[data.kind];
  const { author, contributors } = parseContributors(entityActors);

  return {
    ...topicEntity,
    author,
    createdOn: parseUTCDateTimeFromString(data.createdOn),
    startTime: parseUTCDateTimeFromString(data.startTime),
    endTime: parseUTCDateTimeFromString(data.endTime),
    contributors,
    children: childEntityKind
      ? compact(map(parseEntity(data.topicId, childEntityKind), children))
      : [],
    citations: compact(map(parseEntity(data.topicId, "citation"), children)),
    rank: entityRank ?? 0,
    provider: getProvider(data.provider),
    updatedOn: parseUTCDateTimeFromString(data.updatedOn),
  };
};

export const dedupeDailyUpdates = (entities: TopicEntity[]): TopicEntity[] => {
  const [dailyUpdateEntities, otherEntities] = partition(
    { kind: "daily_summary" },
    entities
  );

  const dailyUpdatesByCreateDate = reduce(
    (obj, entity) => {
      const entityDate = entity.createdOn.startOf("day").toFormat("yyyy-MM-dd");
      const currentEntityForDate = obj[entityDate];
      // use this daily update as the date's daily update if it is the only one
      // so far, or if the previously-identified update included fewer sources
      // than this entity
      const isPrimaryUpdateForDate =
        !currentEntityForDate || currentEntityForDate.endTime < entity.endTime;
      if (isPrimaryUpdateForDate) {
        obj[entityDate] = entity;
      }
      return obj;
    },
    {} as { [key: string]: TopicEntity },
    dailyUpdateEntities
  );

  // will get sorted by the caller so order doesn't really matter
  return [...Object.values(dailyUpdatesByCreateDate), ...otherEntities];
};

export const parseTopicEntities = (data: ApiTopicEntity[]): TopicEntity[] => {
  const parsedEntities = map(parseTopicEntity, data);
  const dedupedEntities = dedupeDailyUpdates(parsedEntities);
  return orderBy(
    (entity) => entity.endTime.toMillis(),
    "desc",
    dedupedEntities
  );
};
